import classNames from 'classnames';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';
import PropTypes from 'prop-types';
import bindAll from 'lodash.bindall';
import bowser from 'bowser';
import React from 'react';
import ReactModal from 'react-modal';

import VM from 'scratch-vm';

import Box from '../box/box.jsx';
import Button from '../button/button.jsx';
import {ComingSoonTooltip} from '../coming-soon/coming-soon.jsx';
import Divider from '../divider/divider.jsx';
import MenuBarMenu from './menu-bar-menu.jsx';
import {MenuItem, MenuSection} from '../menu/menu.jsx';
import ProjectTitleInput from './project-title-input.jsx';
import AuthorInfo from './author-info.jsx';
import LoginDropdown from './login-dropdown.jsx';
import SB3Downloader from '../../containers/sb3-downloader.jsx';
import ProjectBundleDownloader from '../../containers/project-bundle-downloader.jsx';
import ProjectBundleConfirmationModal
    from '../project-bundle-confirmation-modal/project-bundle-confirmation-modal.jsx';
import DeletionRestorer from '../../containers/deletion-restorer.jsx';
import TurboMode from '../../containers/turbo-mode.jsx';
import MenuBarHOC from '../../containers/menu-bar-hoc.jsx';
import SettingsMenu from './settings-menu.jsx';

import {openTipsLibrary, openDebugModal, openVisionTrainer, openCodeLibrary} from '../../reducers/modals';
import {setPlayer} from '../../reducers/mode';
import {
    isTimeTravel220022BC,
    isTimeTravel1920,
    isTimeTravel1990,
    isTimeTravel2020,
    isTimeTravelNow,
    setTimeTravel
} from '../../reducers/time-travel';
import {
    autoUpdateProject,
    getIsUpdating,
    getIsShowingProject,
    manualUpdateProject,
    requestNewProject,
    remixProject,
    saveProjectAsCopy
} from '../../reducers/project-state';
import {
    openAboutMenu,
    closeAboutMenu,
    aboutMenuOpen,
    openAccountMenu,
    closeAccountMenu,
    accountMenuOpen,
    openFileMenu,
    closeFileMenu,
    fileMenuOpen,
    openEditMenu,
    closeEditMenu,
    editMenuOpen,
    openLoginMenu,
    closeLoginMenu,
    loginMenuOpen,
    openModeMenu,
    closeModeMenu,
    modeMenuOpen,
    settingsMenuOpen,
    openSettingsMenu,
    closeSettingsMenu
} from '../../reducers/menus';

import collectMetadata from '../../lib/collect-metadata';

import styles from './menu-bar.css';

import helpIcon from '../../lib/assets/icon--tutorials.svg';
import remixIcon from './icon--remix.svg';
import dropdownCaret from './dropdown-caret.svg';
import aboutIcon from './icon--about.svg';
import fileIcon from './icon--file.svg';
import editIcon from './icon--edit.svg';
import debugIcon from '../debug-modal/icons/icon--debug.svg';
import dataTrainerIcon from '../../lib/libraries/extensions/visionClassifier/vision-classifier-small.svg';
import codeLibraryIcon from '../gui/icon--code.svg';
import microbitIcon from '../../lib/libraries/extensions/microbit/microbit-small.svg';
import datakoderenIcon from './icon--datakoderen.svg';

import scratchLogo from './scratch-logo.svg';
import ninetiesLogo from './nineties_logo.svg';
import catLogo from './cat_logo.svg';
import prehistoricLogo from './prehistoric-logo.svg';
import oldtimeyLogo from './oldtimey-logo.svg';

// eslint-disable-next-line max-len
const radioGatewayOptionIcon = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDMyIDMyIj48ZyBmaWxsPSJub25lIiBzdHJva2U9IiMwZjNlMzQiIHN0cm9rZS13aWR0aD0iMi41IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxwYXRoIGQ9Ik0xMC41IDYuNXY2aDExdi04aC00Ii8+PHBhdGggZD0iTTE0IDYuNXYtMm00IDJ2LTJNMjEgMTYuNWg0YTMgMyAwIDAgMSAzIDN2MGEzIDMgMCAwIDEtMyAzaC00Ii8+PHBhdGggZD0iTTEwLjUgMTAuNWgtNGEzIDMgMCAwIDAtMyAzdjZhMyAzIDAgMCAwIDMgM2g0Ii8+PHBhdGggZD0iTTEyIDE2aDh2NmgtOHoiLz48L2c+PC9zdmc+';
const microbitUsbSerialFilters = [{usbVendorId: 0x0d28}];
const microbitDriveDbName = 'scratch-microbit-usb';
const microbitDriveStoreName = 'handles';
const microbitDriveHandleKey = 'microbit-drive';

const openMicrobitDriveDb = () => new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
        resolve(null);
        return;
    }

    const request = indexedDB.open(microbitDriveDbName, 1);
    request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(microbitDriveStoreName)) {
            db.createObjectStore(microbitDriveStoreName);
        }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Could not open IndexedDB for micro:bit drive cache'));
});

const loadSavedMicrobitDriveHandle = async () => {
    const db = await openMicrobitDriveDb();
    if (!db) {
        return null;
    }

    return new Promise((resolve, reject) => {
        const transaction = db.transaction(microbitDriveStoreName, 'readonly');
        const store = transaction.objectStore(microbitDriveStoreName);
        const request = store.get(microbitDriveHandleKey);
        request.onsuccess = () => {
            db.close();
            resolve(request.result || null);
        };
        request.onerror = () => {
            db.close();
            reject(request.error || new Error('Could not read saved micro:bit drive handle'));
        };
    });
};

const saveMicrobitDriveHandle = async driveHandle => {
    const db = await openMicrobitDriveDb();
    if (!db) {
        return;
    }

    return new Promise((resolve, reject) => {
        const transaction = db.transaction(microbitDriveStoreName, 'readwrite');
        transaction.oncomplete = () => {
            db.close();
            resolve();
        };
        transaction.onerror = () => {
            db.close();
            reject(transaction.error || new Error('Could not save micro:bit drive handle'));
        };
        transaction.objectStore(microbitDriveStoreName).put(driveHandle, microbitDriveHandleKey);
    });
};

const clearSavedMicrobitDriveHandle = async () => {
    const db = await openMicrobitDriveDb();
    if (!db) {
        return;
    }

    return new Promise((resolve, reject) => {
        const transaction = db.transaction(microbitDriveStoreName, 'readwrite');
        transaction.oncomplete = () => {
            db.close();
            resolve();
        };
        transaction.onerror = () => {
            db.close();
            reject(transaction.error || new Error('Could not clear saved micro:bit drive handle'));
        };
        transaction.objectStore(microbitDriveStoreName).delete(microbitDriveHandleKey);
    });
};

const ensureDriveHandlePermission = async driveHandle => {
    if (!driveHandle || typeof driveHandle.queryPermission !== 'function') {
        return Boolean(driveHandle);
    }

    let permission = await driveHandle.queryPermission({mode: 'readwrite'});
    if (permission !== 'granted' && typeof driveHandle.requestPermission === 'function') {
        permission = await driveHandle.requestPermission({mode: 'readwrite'});
    }

    return permission === 'granted';
};

import sharedMessages from '../../lib/shared-messages';

const ariaMessages = defineMessages({
    tutorials: {
        id: 'gui.menuBar.tutorialsLibrary',
        defaultMessage: 'Tutorials',
        description: 'accessibility text for the tutorials button'
    },
    debug: {
        id: 'gui.menuBar.debug',
        defaultMessage: 'Debug',
        description: 'accessibility text for the debug button'
    },
    dataTrainer: {
        id: 'gui.menuBar.dataTrainer',
        defaultMessage: 'Datatræner',
        description: 'accessibility text for the data trainer button'
    },
    codeLibrary: {
        id: 'gui.menuBar.codeLibrary',
        defaultMessage: 'Kodebibliotek',
        description: 'accessibility text for the code library button'
    },
    connectMicrobit: {
        id: 'gui.menuBar.connectMicrobit',
        defaultMessage: 'micro:bit (Bluetooth)',
        description: 'accessibility text for the micro:bit BLE connection button'
    },
    connectRadioGateway: {
        id: 'gui.menuBar.connectRadioGateway',
        defaultMessage: 'Radio gateway (USB)',
        description: 'accessibility text for the radio gateway USB connection button'
    },
    microbitChooserTitle: {
        id: 'gui.menuBar.microbitChooserTitle',
        defaultMessage: 'VÃ¦lg hvordan du vil forbinde micro:bit',
        description: 'title for the micro:bit connection chooser modal'
    },
    microbitChooserBody: {
        id: 'gui.menuBar.microbitChooserBody',
        defaultMessage: 'VÃ¦lg den forbindelse der passer til det, du vil bruge micro:bit til.',
        description: 'intro text for the micro:bit connection chooser modal'
    },
    radioGatewayDescription: {
        id: 'gui.menuBar.radioGatewayDescription',
        defaultMessage: 'Brug kabel og radio-gateway til radioblokke og kommunikation mellem micro:bits.',
        description: 'description for the radio gateway connection option'
    },
    connectMicrobitDescription: {
        id: 'gui.menuBar.connectMicrobitDescription',
        defaultMessage: 'Brug almindelig micro:bit-forbindelse til knapper, bevÃ¦gelse, display og andre standardblokke.',
        description: 'description for the standard BLE micro:bit connection option'
    },
    chooseConnection: {
        id: 'gui.menuBar.chooseConnection',
        defaultMessage: 'VÃ¦lg forbindelse',
        description: 'label for choosing a connection option'
    },
    cancelConnectionChoice: {
        id: 'gui.menuBar.cancelConnectionChoice',
        defaultMessage: 'Luk',
        description: 'close button for the micro:bit connection chooser modal'
    }
});

const MenuBarItemTooltip = ({
    children,
    className,
    enable,
    id,
    place = 'bottom'
}) => {
    if (enable) {
        return (
            <React.Fragment>
                {children}
            </React.Fragment>
        );
    }
    return (
        <ComingSoonTooltip
            className={classNames(styles.comingSoon, className)}
            place={place}
            tooltipClassName={styles.comingSoonTooltip}
            tooltipId={id}
        >
            {children}
        </ComingSoonTooltip>
    );
};


MenuBarItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    enable: PropTypes.bool,
    id: PropTypes.string,
    place: PropTypes.oneOf(['top', 'bottom', 'left', 'right'])
};

const MenuItemTooltip = ({id, isRtl, children, className}) => (
    <ComingSoonTooltip
        className={classNames(styles.comingSoon, className)}
        isRtl={isRtl}
        place={isRtl ? 'left' : 'right'}
        tooltipClassName={styles.comingSoonTooltip}
        tooltipId={id}
    >
        {children}
    </ComingSoonTooltip>
);

MenuItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    id: PropTypes.string,
    isRtl: PropTypes.bool
};

const AboutButton = props => (
    <Button
        className={classNames(styles.menuBarItem, styles.hoverable)}
        iconClassName={styles.aboutIcon}
        iconSrc={aboutIcon}
        onClick={props.onClick}
    />
);

AboutButton.propTypes = {
    onClick: PropTypes.func.isRequired
};

class MenuBar extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            microbitDialogOpen: false,
            microbitUsbSetupBusy: false,
            microbitUsbSetupError: null,
            microbitUsbSetupOpen: false,
            microbitUsbSetupStatus: null,
            microbitUsbFirmwareInstalled: false,
            projectBundleConfirmationOpen: false
        };
        bindAll(this, [
            'handleCancelProjectBundleConfirmation',
            'handleClickNew',
            'handleClickRemix',
            'handleClickSave',
            'handleClickSaveAsCopy',
            'handleClickSeeCommunity',
            'handleClickShare',
            'handleConfirmProjectBundleDownload',
            'handleOpenProjectBundleConfirmation',
            'handleSetMode',
            'handleKeyPress',
            'handleRestoreOption',
            'handleOpenMicrobitMenu',
            'handleCloseMicrobitMenu',
            'handleCloseMicrobitUsbSetup',
            'handleConnectRadioGateway',
            'handleConnectMicrobitBle',
            'handleInstallMicrobitUsbFirmware',
            'handleInstallMicrobitUsbFirmwareAutomatic',
            'handleInstallMicrobitUsbFirmwareWebUsbPrimary',
            'handleInstallMicrobitUsbFirmwareFallback',
            'handleConnectMicrobitUsb',
            'getMicrobitPeripheral',
            'renderMicrobitChooserModal',
            'renderMicrobitUsbSetupModal',
            'getSaveToComputerHandler',
            'restoreOptionMessage'
        ]);
    }
    componentDidMount () {
        document.addEventListener('keydown', this.handleKeyPress);
    }
    componentWillUnmount () {
        document.removeEventListener('keydown', this.handleKeyPress);
    }
    handleClickNew () {
        // if the project is dirty, and user owns the project, we will autosave.
        // but if they are not logged in and can't save, user should consider
        // downloading or logging in first.
        // Note that if user is logged in and editing someone else's project,
        // they'll lose their work.
        const readyToReplaceProject = this.props.confirmReadyToReplaceProject(
            this.props.intl.formatMessage(sharedMessages.replaceProjectWarning)
        );
        this.props.onRequestCloseFile();
        if (readyToReplaceProject) {
            this.props.onClickNew(this.props.canSave && this.props.canCreateNew);
        }
        this.props.onRequestCloseFile();
    }
    handleClickRemix () {
        this.props.onClickRemix();
        this.props.onRequestCloseFile();
    }
    handleClickSave () {
        this.props.onClickSave();
        this.props.onRequestCloseFile();
    }
    handleClickSaveAsCopy () {
        this.props.onClickSaveAsCopy();
        this.props.onRequestCloseFile();
    }
    handleOpenProjectBundleConfirmation () {
        this.props.onRequestCloseFile();
        this.setState({
            projectBundleConfirmationOpen: true
        });
    }
    handleCancelProjectBundleConfirmation () {
        this.setState({
            projectBundleConfirmationOpen: false
        });
    }
    async handleConfirmProjectBundleDownload () {
        this.setState({
            projectBundleConfirmationOpen: false
        });
        if (this.projectBundleDownloadCallback) {
            await this.projectBundleDownloadCallback();
        }
        if (this.props.onProjectTelemetryEvent) {
            const metadata = collectMetadata(this.props.vm, this.props.projectTitle, this.props.locale);
            this.props.onProjectTelemetryEvent('projectDidSave', metadata);
        }
    }
    handleClickSeeCommunity (waitForUpdate) {
        if (this.props.shouldSaveBeforeTransition()) {
            this.props.autoUpdateProject(); // save before transitioning to project page
            waitForUpdate(true); // queue the transition to project page
        } else {
            waitForUpdate(false); // immediately transition to project page
        }
    }
    handleClickShare (waitForUpdate) {
        if (!this.props.isShared) {
            if (this.props.canShare) { // save before transitioning to project page
                this.props.onShare();
            }
            if (this.props.canSave) { // save before transitioning to project page
                this.props.autoUpdateProject();
                waitForUpdate(true); // queue the transition to project page
            } else {
                waitForUpdate(false); // immediately transition to project page
            }
        }
    }
    handleSetMode (mode) {
        return () => {
            // Turn on/off filters for modes.
            if (mode === '1920') {
                document.documentElement.style.filter = 'brightness(.9)contrast(.8)sepia(1.0)';
                document.documentElement.style.height = '100%';
            } else if (mode === '1990') {
                document.documentElement.style.filter = 'hue-rotate(40deg)';
                document.documentElement.style.height = '100%';
            } else {
                document.documentElement.style.filter = '';
                document.documentElement.style.height = '';
            }

            // Change logo for modes
            const logoElement = document.getElementById('logo_img');
            if (logoElement && logoElement.tagName === 'IMG') {
                if (mode === '1990') {
                    logoElement.src = ninetiesLogo;
                } else if (mode === '2020') {
                    logoElement.src = catLogo;
                } else if (mode === '1920') {
                    logoElement.src = oldtimeyLogo;
                } else if (mode === '220022BC') {
                    logoElement.src = prehistoricLogo;
                } else {
                    logoElement.src = this.props.logo;
                }
            }

            this.props.onSetTimeTravelMode(mode);
        };
    }
    handleRestoreOption (restoreFun) {
        return () => {
            restoreFun();
            this.props.onRequestCloseEdit();
        };
    }
    handleKeyPress (event) {
        const modifier = bowser.mac ? event.metaKey : event.ctrlKey;
        if (modifier && event.key === 's') {
            this.props.onClickSave();
            event.preventDefault();
        }
    }
    handleOpenMicrobitMenu () {
        this.handleConnectMicrobitBle();
    }
    handleCloseMicrobitMenu () {
        this.setState({microbitDialogOpen: false});
    }
    handleCloseMicrobitUsbSetup () {
        this.setState({
            microbitUsbSetupOpen: false,
            microbitUsbSetupBusy: false,
            microbitUsbSetupError: null,
            microbitUsbSetupStatus: null
        });
    }
    async getMicrobitPeripheral () {
        if (this.props.onPrepareMicrobitPeripheral) {
            return this.props.onPrepareMicrobitPeripheral(this.props.vm);
        }

        if (!this.props.vm.extensionManager.isExtensionLoaded('microbit')) {
            await this.props.vm.extensionManager.loadExtensionURL('microbit');
        }

        return this.props.vm.runtime && this.props.vm.runtime.peripheralExtensions &&
            this.props.vm.runtime.peripheralExtensions.microbit;
    }
    handleConnectRadioGateway () {
        this.handleCloseMicrobitMenu();
        this.props.onMicrobitButtonClick(
            this.props.vm,
            this.props.vm.getPeripheralIsConnected('microbit')
        );
    }
    handleConnectMicrobitBle () {
        this.handleCloseMicrobitMenu();
        const isConnected = this.props.vm.getPeripheralIsConnected('microbit');
        if (isConnected) {
            this.props.vm.disconnectPeripheral('microbit');
            return;
        }

        const peripheral = this.props.vm.runtime && this.props.vm.runtime.peripheralExtensions &&
            this.props.vm.runtime.peripheralExtensions.microbit;
        const firmwareInstalled = peripheral &&
            typeof peripheral.hasUsbMicrobitFirmwareHint === 'function' &&
            peripheral.hasUsbMicrobitFirmwareHint();

        this.setState({
            microbitUsbSetupBusy: false,
            microbitUsbSetupError: null,
            microbitUsbSetupOpen: true,
            microbitUsbSetupStatus: firmwareInstalled ?
                'Firmwaren ser allerede ud til at være installeret. Du kan gå direkte til trin 2, eller installere igen for en sikkerheds skyld.' :
                'Første gang skal vi lige gøre to ting: 1) lægge firmware på via MICROBIT-drevet og 2) åbne kabel-forbindelsen.',
            microbitUsbFirmwareInstalled: Boolean(firmwareInstalled)
        });
    }
    async handleInstallMicrobitUsbFirmware () {
        return this.handleInstallMicrobitUsbFirmwareWebUsbPrimary();

        if (this.state.microbitUsbSetupBusy) {
            return;
        }

        try {
            if (typeof window === 'undefined' || typeof window.showDirectoryPicker !== 'function') {
                throw new Error('Denne browser understÃ¸tter ikke automatisk firmware-installation til MICROBIT-drevet.');
            }

            this.setState({
                microbitUsbSetupBusy: true,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'VÃ¦lg MICROBIT-drevet i nÃ¦ste vindue. Databygger kopierer derefter firmwaren automatisk.'
            });

            const driveHandle = await window.showDirectoryPicker({mode: 'readwrite'});
            const peripheral = await this.getMicrobitPeripheral();
            if (!peripheral || typeof peripheral.installOfficialUsbFirmware !== 'function') {
                throw new Error('micro:bit-forbindelsen er ikke klar endnu. PrÃ¸v igen om et Ã¸jeblik.');
            }

            await peripheral.installOfficialUsbFirmware(driveHandle);
            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'Firmwaren er kopieret. Vent til micro:bit genstarter, og tryk derefter pÃ¥ "Forbind via kabel".',
                microbitUsbFirmwareInstalled: true
            });
        } catch (error) {
            if (error && (error.name === 'AbortError' || error.name === 'NotFoundError')) {
                this.setState({
                    microbitUsbSetupBusy: false,
                    microbitUsbSetupStatus: 'Installationen blev annulleret. Du kan prøve igen, når du er klar.'
                });
                return;
            }

            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: error && error.message ? error.message : 'Kunne ikke installere firmware pÃ¥ micro:bit.'
            });
        }
    }
    async handleInstallMicrobitUsbFirmwareAutomatic () {
        return this.handleInstallMicrobitUsbFirmwareWebUsbPrimary();

        if (this.state.microbitUsbSetupBusy) {
            return;
        }

        try {
            this.setState({
                microbitUsbSetupBusy: true,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'VÃ¦lg din micro:bit i browserens USB-vindue. Databygger overfÃ¸rer derefter firmwaren direkte via USB.'
            });

            const peripheral = await this.getMicrobitPeripheral();
            if (!peripheral || typeof peripheral.installOfficialUsbFirmware !== 'function') {
                throw new Error('micro:bit-forbindelsen er ikke klar endnu. PrÃ¸v igen om et Ã¸jeblik.');
            }

            await peripheral.installOfficialUsbFirmware();
            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'Firmwaren er overfÃ¸rt direkte via USB. Vent til micro:bit genstarter, og tryk derefter pÃ¥ "Forbind via kabel".',
                microbitUsbFirmwareInstalled: true
            });
            return;
        } catch (error) {
            if (error && (error.name === 'AbortError' || error.name === 'NotFoundError')) {
                this.setState({
                    microbitUsbSetupBusy: false,
                    microbitUsbSetupStatus: 'Installationen blev annulleret. Du kan prøve igen, når du er klar.'
                });
                return;
            }

            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: error && error.message ? error.message : 'Kunne ikke installere firmware via WebUSB.'
            });
            return;
        }

        try {
            this.setState({
                microbitUsbSetupBusy: true,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'VÃ¦lg din micro:bit i browserens USB-vindue. Databygger overfÃ¸rer derefter firmwaren direkte via WebUSB.'
            });

            const peripheral = await this.getMicrobitPeripheral();
            if (!peripheral || typeof peripheral.installOfficialUsbFirmware !== 'function') {
                throw new Error('micro:bit-forbindelsen er ikke klar endnu. PrÃ¸v igen om et Ã¸jeblik.');
            }

            await peripheral.installOfficialUsbFirmware();
            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'Firmwaren er overfÃ¸rt direkte via WebUSB. Vent til micro:bit genstarter, og tryk derefter pÃ¥ "Forbind via kabel".',
                microbitUsbFirmwareInstalled: true
            });
            return;
        } catch (error) {
            if (error && (error.name === 'AbortError' || error.name === 'NotFoundError')) {
                this.setState({
                    microbitUsbSetupBusy: false,
                    microbitUsbSetupStatus: 'WebUSB-installationen blev annulleret. Du kan prøve igen, når du er klar.'
                });
                return;
            }

            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: error && error.message ? error.message : 'Kunne ikke installere firmware via WebUSB.'
            });
            return;
        }

        try {
            this.setState({
                microbitUsbSetupBusy: true,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'VÃ¦lg din micro:bit i browserens USB-vindue. Databygger overfÃ¸rer derefter firmwaren direkte via WebUSB.'
            });

            const peripheral = await this.getMicrobitPeripheral();
            if (!peripheral || typeof peripheral.installOfficialUsbFirmware !== 'function') {
                throw new Error('micro:bit-forbindelsen er ikke klar endnu. PrÃ¸v igen om et Ã¸jeblik.');
            }

            await peripheral.installOfficialUsbFirmware();
            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'Firmwaren er overfÃ¸rt direkte via WebUSB. Vent til micro:bit genstarter, og tryk derefter pÃ¥ "Forbind via kabel".',
                microbitUsbFirmwareInstalled: true
            });
            return;
        } catch (error) {
            if (error && (error.name === 'AbortError' || error.name === 'NotFoundError')) {
                this.setState({
                    microbitUsbSetupBusy: false,
                    microbitUsbSetupStatus: 'WebUSB-installationen blev annulleret. Du kan prøve igen, når du er klar.'
                });
                return;
            }

            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: error && error.message ? error.message : 'Kunne ikke installere firmware via WebUSB.'
            });
            return;
        }

        try {
            this.setState({
                microbitUsbSetupBusy: true,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'Forbereder firmware-overførslen til micro:bit...'
            });

            let driveHandle = await loadSavedMicrobitDriveHandle();
            let usingSavedDrive = false;
            if (driveHandle && await ensureDriveHandlePermission(driveHandle)) {
                usingSavedDrive = true;
                this.setState({
                    microbitUsbSetupStatus: 'Bruger det gemte MICROBIT-drev og overfører firmwaren automatisk...'
                });
            } else {
                if (typeof window === 'undefined' || typeof window.showDirectoryPicker !== 'function') {
                    throw new Error('Denne browser understøtter ikke automatisk firmware-installation til MICROBIT-drevet.');
                }
                this.setState({
                    microbitUsbSetupStatus: 'Vælg MICROBIT-drevet i næste vindue. Databygger husker det derefter automatisk.'
                });
                driveHandle = await window.showDirectoryPicker({mode: 'readwrite'});
            }

            const peripheral = await this.getMicrobitPeripheral();
            if (!peripheral || typeof peripheral.installOfficialUsbFirmware !== 'function') {
                throw new Error('micro:bit-forbindelsen er ikke klar endnu. Prøv igen om et øjeblik.');
            }

            await peripheral.installOfficialUsbFirmware(driveHandle);
            if (!usingSavedDrive) {
                try {
                    await saveMicrobitDriveHandle(driveHandle);
                } catch (saveError) {
                    // Ignore cache-save failures. The transfer itself succeeded.
                }
            }

            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: usingSavedDrive ?
                    'Firmwaren blev overført direkte til det gemte MICROBIT-drev. Vent til micro:bit genstarter, og tryk derefter på "Forbind via kabel".' :
                    'Firmwaren er kopieret. Fremover husker Databygger dette drev automatisk. Vent til micro:bit genstarter, og tryk derefter på "Forbind via kabel".',
                microbitUsbFirmwareInstalled: true
            });
        } catch (error) {
            if (error && (error.name === 'AbortError' || error.name === 'NotFoundError')) {
                this.setState({
                    microbitUsbSetupBusy: false,
                    microbitUsbSetupStatus: 'Installationen blev annulleret. Du kan prøve igen, når du er klar.'
                });
                return;
            }

            try {
                await clearSavedMicrobitDriveHandle();
            } catch (clearError) {
                // Ignore cache-clear failures.
            }
            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: error && error.message ? error.message : 'Kunne ikke installere firmware på micro:bit.'
            });
        }
    }
    async handleInstallMicrobitUsbFirmwareAutomatic () {
        return this.handleInstallMicrobitUsbFirmwareWebUsbPrimary();

        if (this.state.microbitUsbSetupBusy) {
            return;
        }

        try {
            this.setState({
                microbitUsbSetupBusy: true,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'VÃ¦lg din micro:bit i nÃ¦ste vindue. Databygger overfÃ¸rer derefter firmwaren direkte via USB.'
            });

            const peripheral = await this.getMicrobitPeripheral();
            if (!peripheral || typeof peripheral.installOfficialUsbFirmware !== 'function') {
                throw new Error('micro:bit-forbindelsen er ikke klar endnu. PrÃ¸v igen om et Ã¸jeblik.');
            }

            await peripheral.installOfficialUsbFirmware();
            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'Firmwaren er overfÃ¸rt direkte til micro:bit. Vent til micro:bit genstarter, og tryk derefter pÃ¥ "Forbind via kabel".',
                microbitUsbFirmwareInstalled: true
            });
        } catch (error) {
            if (error && (error.name === 'AbortError' || error.name === 'NotFoundError')) {
                this.setState({
                    microbitUsbSetupBusy: false,
                    microbitUsbSetupStatus: 'Firmware-overførslen blev annulleret. Du kan prøve igen, når du er klar.'
                });
                return;
            }

            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: error && error.message ? error.message : 'Kunne ikke overfÃ¸re firmware til micro:bit.'
            });
        }
    }

    async handleInstallMicrobitUsbFirmwareFallback () {
        if (this.state.microbitUsbSetupBusy) {
            return;
        }

        try {
            if (typeof window === 'undefined' || typeof window.showDirectoryPicker !== 'function') {
                throw new Error('Denne browser understÃ¸tter ikke fallback via MICROBIT-drevet.');
            }

            this.setState({
                microbitUsbSetupBusy: true,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'VÃ¦lg MICROBIT-drevet i nÃ¦ste vindue. Databygger kopierer derefter firmwaren som fallback.'
            });

            let driveHandle = await loadSavedMicrobitDriveHandle();
            let usingSavedDrive = false;
            if (driveHandle && await ensureDriveHandlePermission(driveHandle)) {
                usingSavedDrive = true;
                this.setState({
                    microbitUsbSetupStatus: 'Bruger det gemte MICROBIT-drev og kopierer firmwaren som fallback...'
                });
            } else {
                driveHandle = await window.showDirectoryPicker({mode: 'readwrite'});
            }

            const peripheral = await this.getMicrobitPeripheral();
            if (!peripheral || typeof peripheral.installOfficialUsbFirmwareViaDrive !== 'function') {
                throw new Error('micro:bit-forbindelsen er ikke klar endnu. PrÃ¸v igen om et Ã¸jeblik.');
            }

            await peripheral.installOfficialUsbFirmwareViaDrive(driveHandle);
            if (!usingSavedDrive) {
                try {
                    await saveMicrobitDriveHandle(driveHandle);
                } catch (saveError) {
                    // Ignore cache-save failures.
                }
            }

            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'Firmwaren er kopieret via MICROBIT-drevet. Vent til micro:bit genstarter, og tryk derefter pÃ¥ "Forbind via kabel".',
                microbitUsbFirmwareInstalled: true
            });
        } catch (error) {
            if (error && (error.name === 'AbortError' || error.name === 'NotFoundError')) {
                this.setState({
                    microbitUsbSetupBusy: false,
                    microbitUsbSetupStatus: 'Fallback-installationen blev annulleret. Du kan prøve igen, når du er klar.'
                });
                return;
            }

            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: error && error.message ? error.message : 'Kunne ikke installere firmware via MICROBIT-drevet.'
            });
        }
    }

    async handleInstallMicrobitUsbFirmwareAutomatic () {
        return this.handleInstallMicrobitUsbFirmwareWebUsbPrimary();

        if (this.state.microbitUsbSetupBusy) {
            return;
        }

        try {
            this.setState({
                microbitUsbSetupBusy: true,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'Forbereder firmware-overførslen til micro:bit...'
            });

            let driveHandle = await loadSavedMicrobitDriveHandle();
            let usingSavedDrive = false;
            if (driveHandle && await ensureDriveHandlePermission(driveHandle)) {
                usingSavedDrive = true;
                this.setState({
                    microbitUsbSetupStatus: 'Bruger det gemte MICROBIT-drev og kopierer firmwaren automatisk...'
                });
            } else {
                if (typeof window === 'undefined' || typeof window.showDirectoryPicker !== 'function') {
                    throw new Error('Denne browser understÃ¸tter ikke automatisk firmware-installation til MICROBIT-drevet.');
                }
                this.setState({
                    microbitUsbSetupStatus: 'VÃ¦lg MICROBIT-drevet i nÃ¦ste vindue. Databygger husker det derefter automatisk.'
                });
                driveHandle = await window.showDirectoryPicker({mode: 'readwrite'});
            }

            const peripheral = await this.getMicrobitPeripheral();
            if (!peripheral || typeof peripheral.installOfficialUsbFirmwareViaDrive !== 'function') {
                throw new Error('micro:bit-forbindelsen er ikke klar endnu. PrÃ¸v igen om et Ã¸jeblik.');
            }

            await peripheral.installOfficialUsbFirmwareViaDrive(driveHandle);
            if (!usingSavedDrive) {
                try {
                    await saveMicrobitDriveHandle(driveHandle);
                } catch (saveError) {
                    // Ignore cache-save failures. The transfer itself succeeded.
                }
            }

            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: usingSavedDrive ?
                    'Firmwaren blev kopieret direkte til det gemte MICROBIT-drev. Vent til micro:bit genstarter, og tryk derefter pÃ¥ "Forbind via kabel".' :
                    'Firmwaren er kopieret. Fremover husker Databygger dette drev automatisk. Vent til micro:bit genstarter, og tryk derefter pÃ¥ "Forbind via kabel".',
                microbitUsbFirmwareInstalled: true
            });
        } catch (error) {
            if (error && (error.name === 'AbortError' || error.name === 'NotFoundError')) {
                this.setState({
                    microbitUsbSetupBusy: false,
                    microbitUsbSetupStatus: 'Installationen blev annulleret. Du kan prøve igen, når du er klar.'
                });
                return;
            }

            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: error && error.message ? error.message : 'Kunne ikke installere firmware pÃ¥ micro:bit.'
            });
        }
    }

    async handleInstallMicrobitUsbFirmwareWebUsbPrimary () {
        if (this.state.microbitUsbSetupBusy) {
            return;
        }

        try {
            this.setState({
                microbitUsbSetupBusy: true,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'Vælg din micro:bit i næste vindue. Så lægger Databygger firmwaren på.'
            });

            const peripheral = await this.getMicrobitPeripheral();
            if (!peripheral || typeof peripheral.installOfficialUsbFirmware !== 'function') {
                throw new Error('micro:bit-forbindelsen er ikke klar endnu. Prøv igen om et øjeblik.');
            }

            await peripheral.installOfficialUsbFirmware();
            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'Firmwaren er lagt på. Vent et øjeblik, og tryk så på "Forbind med kabel".',
                microbitUsbFirmwareInstalled: true
            });
        } catch (error) {
            if (error && (error.name === 'AbortError' || error.name === 'NotFoundError')) {
                this.setState({
                    microbitUsbSetupBusy: false,
                    microbitUsbSetupStatus: 'Installationen blev annulleret. Du kan prøve igen, når du er klar.'
                });
                return;
            }

            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: error && error.message ? error.message : 'Kunne ikke installere firmware via WebUSB.'
            });
        }
    }

    async handleConnectMicrobitUsb () {
        if (this.state.microbitUsbSetupBusy) {
            return;
        }

        try {
            if (typeof navigator === 'undefined' || !navigator.serial ||
                typeof navigator.serial.requestPort !== 'function') {
                throw new Error('Web Serial er ikke tilgÃ¦ngelig i denne browser.');
            }

            this.setState({
                microbitUsbSetupBusy: true,
                microbitUsbSetupError: null,
                microbitUsbSetupStatus: 'Vælg din micro:bit i næste vindue for at forbinde den med kabel.'
            });

            const port = await navigator.serial.requestPort({
                filters: microbitUsbSerialFilters
            });
            const peripheral = await this.getMicrobitPeripheral();
            if (!peripheral || typeof peripheral.connectOfficialUsbPort !== 'function') {
                throw new Error('micro:bit-forbindelsen er ikke klar endnu. PrÃ¸v igen om et Ã¸jeblik.');
            }

            await peripheral.connectOfficialUsbPort(port);
            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: null,
                microbitUsbSetupOpen: false,
                microbitUsbSetupStatus: null
            });
        } catch (error) {
            if (error && (error.name === 'AbortError' || error.name === 'NotFoundError')) {
                this.setState({
                    microbitUsbSetupBusy: false,
                    microbitUsbSetupStatus: 'Kabel-forbindelsen blev annulleret. Du kan prøve igen, når micro:bit er klar.'
                });
                return;
            }

            this.setState({
                microbitUsbSetupBusy: false,
                microbitUsbSetupError: error && error.message ? error.message : 'Kunne ikke Ã¥bne kabel-forbindelsen til micro:bit.'
            });
        }
    }
    handleConnectMicrobitBle () {
        this.handleCloseMicrobitMenu();
        const isConnected = this.props.vm.getPeripheralIsConnected('microbit');
        if (isConnected) {
            this.props.vm.disconnectPeripheral('microbit');
            return;
        }

        const peripheral = this.props.vm.runtime && this.props.vm.runtime.peripheralExtensions &&
            this.props.vm.runtime.peripheralExtensions.microbit;
        const firmwareInstalled = peripheral &&
            typeof peripheral.hasUsbMicrobitFirmwareHint === 'function' &&
            peripheral.hasUsbMicrobitFirmwareHint();

        this.setState({
            microbitUsbSetupBusy: false,
            microbitUsbSetupError: null,
            microbitUsbSetupOpen: true,
            microbitUsbSetupStatus: firmwareInstalled ?
                'Det ser ud til, at firmware allerede er på micro:bit. Du kan gå videre til trin 2 eller installere igen.' :
                'Vi skal gøre to ting: 1) lægge firmware på micro:bit og 2) forbinde den med kabel.',
            microbitUsbFirmwareInstalled: Boolean(firmwareInstalled)
        });
    }

    renderMicrobitChooserModal () {
        return (
            <ReactModal
                isOpen={this.state.microbitDialogOpen}
                className={styles.microbitModalContent}
                contentLabel={this.props.intl.formatMessage(ariaMessages.microbitChooserTitle)}
                overlayClassName={styles.microbitModalOverlay}
                onRequestClose={this.handleCloseMicrobitMenu}
            >
                <div
                    className={styles.microbitModalBody}
                    dir={this.props.isRtl ? 'rtl' : 'ltr'}
                >
                    <div className={styles.microbitModalHeader}>
                        <img
                            className={styles.microbitModalHeaderIcon}
                            src={microbitIcon}
                        />
                        <div>
                            <div className={styles.microbitModalTitle}>
                                <FormattedMessage {...ariaMessages.microbitChooserTitle} />
                            </div>
                            <div className={styles.microbitModalDescription}>
                                <FormattedMessage
                                    id="gui.menuBar.microbitChooserBodyClarified"
                                    defaultMessage="VÃ¦lg den forbindelse der passer til det, du vil bruge micro:bit til. Den almindelige micro:bit-forbindelse virker nu direkte over kabel."
                                    description="clarified intro text for the micro:bit connection chooser modal"
                                />
                            </div>
                        </div>
                    </div>
                    <div className={styles.microbitChoiceGrid}>
                        <button
                            className={styles.microbitChoiceCard}
                            onClick={this.handleConnectMicrobitBle}
                        >
                            <div className={styles.microbitChoiceIconWrap}>
                                <img
                                    className={styles.microbitChoiceIcon}
                                    src={microbitIcon}
                                />
                            </div>
                            <div className={styles.microbitChoiceTitle}>
                                <FormattedMessage
                                    id="gui.menuBar.connectMicrobitUsb"
                                    defaultMessage="micro:bit (USB)"
                                    description="label for the standard micro:bit usb connection option"
                                />
                            </div>
                            <div className={styles.microbitChoiceDescription}>
                                <FormattedMessage
                                    id="gui.menuBar.connectMicrobitUsbDescription"
                                    defaultMessage="Brug USB-kabel til knapper, bevÃ¦gelse, display og andre standardblokke. Ingen Bluetooth nÃ¸dvendig."
                                    description="description for the standard USB micro:bit connection option"
                                />
                            </div>
                            <div className={styles.microbitChoiceAction}>
                                <FormattedMessage {...ariaMessages.chooseConnection} />
                            </div>
                        </button>
                        <button
                            className={styles.microbitChoiceCard}
                            onClick={this.handleConnectRadioGateway}
                        >
                            <div className={styles.microbitChoiceIconWrap}>
                                <img
                                    className={styles.microbitChoiceIcon}
                                    src={radioGatewayOptionIcon}
                                />
                            </div>
                            <div className={styles.microbitChoiceTitle}>
                                <FormattedMessage {...ariaMessages.connectRadioGateway} />
                            </div>
                            <div className={styles.microbitChoiceDescription}>
                                <FormattedMessage
                                    id="gui.menuBar.radioGatewayDescriptionClarified"
                                    defaultMessage="Brug USB-kabel hele vejen. VÃ¦lg denne hvis din micro:bit sidder i kabel og du vil bruge radio."
                                    description="clarified description for the radio gateway connection option"
                                />
                            </div>
                            <div className={styles.microbitChoiceAction}>
                                <FormattedMessage {...ariaMessages.chooseConnection} />
                            </div>
                        </button>
                    </div>
                    <div className={styles.microbitModalFooter}>
                        <button
                            className={styles.microbitModalCloseButton}
                            onClick={this.handleCloseMicrobitMenu}
                        >
                            <FormattedMessage {...ariaMessages.cancelConnectionChoice} />
                        </button>
                    </div>
                </div>
            </ReactModal>
        );
    }
    renderMicrobitUsbSetupModal () {
        return (
            <ReactModal
                isOpen={this.state.microbitUsbSetupOpen}
                className={styles.microbitModalContent}
                contentLabel={this.props.intl.formatMessage({
                    id: 'gui.menuBar.microbitUsbSetupTitle',
                    defaultMessage: 'Forbind micro:bit via USB',
                    description: 'title for the guided usb micro:bit setup modal'
                })}
                overlayClassName={styles.microbitModalOverlay}
                onRequestClose={this.handleCloseMicrobitUsbSetup}
            >
                <div
                    className={styles.microbitModalBody}
                    dir={this.props.isRtl ? 'rtl' : 'ltr'}
                >
                    <div className={styles.microbitModalHeader}>
                        <img
                            className={styles.microbitModalHeaderIcon}
                            src={microbitIcon}
                        />
                        <div>
                            <div className={styles.microbitModalTitle}>
                                <FormattedMessage
                                    id="gui.menuBar.microbitUsbSetupTitle"
                                    defaultMessage="Forbind micro:bit"
                                    description="title for the guided usb micro:bit setup modal"
                                />
                            </div>
                            <div className={styles.microbitModalDescription}>
                                <FormattedMessage
                                    id="gui.menuBar.microbitUsbSetupDescription"
                                    defaultMessage="Følg de to trin herunder. Først lægger vi firmware på micro:bit, og bagefter forbinder vi den."
                                    description="description for the guided usb micro:bit setup modal"
                                />
                            </div>
                        </div>
                    </div>
                    <div className={styles.microbitSetupGrid}>
                        <div className={styles.microbitSetupCard}>
                            <div className={styles.microbitSetupStepNumber}>1</div>
                            <div className={styles.microbitSetupTitle}>
                                <FormattedMessage
                                    id="gui.menuBar.microbitUsbInstallTitle"
                                    defaultMessage="Læg firmware på"
                                    description="title for the install firmware step in the guided usb micro:bit setup modal"
                                />
                            </div>
                            <div className={styles.microbitSetupText}>
                                <FormattedMessage
                                    id="gui.menuBar.microbitUsbInstallDescription"
                                    defaultMessage="Vælg MICROBIT-drevet. Så lægger Databygger firmwaren over på din micro:bit."
                                    description="description for the install firmware step in the guided usb micro:bit setup modal"
                                />
                            </div>
                            <button
                                className={styles.microbitSetupPrimaryButton}
                                disabled={this.state.microbitUsbSetupBusy}
                                onClick={this.handleInstallMicrobitUsbFirmwareWebUsbPrimary}
                            >
                                <FormattedMessage
                                    id="gui.menuBar.microbitUsbInstallAction"
                                    defaultMessage="Læg firmware på"
                                    description="button label for firmware installation via the MICROBIT drive"
                                />
                            </button>
                        </div>
                        <div className={styles.microbitSetupCard}>
                            <div className={styles.microbitSetupStepNumber}>2</div>
                            <div className={styles.microbitSetupTitle}>
                                <FormattedMessage
                                    id="gui.menuBar.microbitUsbConnectTitle"
                                    defaultMessage="Forbind med kabel"
                                    description="title for the connect via cable step in the guided usb micro:bit setup modal"
                                />
                            </div>
                            <div className={styles.microbitSetupText}>
                                <FormattedMessage
                                    id="gui.menuBar.microbitUsbConnectDescription"
                                    defaultMessage="Når micro:bit er startet igen, vælger du den i næste vindue. Så er den klar til at virke."
                                    description="description for the connect via cable step in the guided usb micro:bit setup modal"
                                />
                            </div>
                            <button
                                className={styles.microbitSetupPrimaryButton}
                                disabled={this.state.microbitUsbSetupBusy || !this.state.microbitUsbFirmwareInstalled}
                                onClick={this.handleConnectMicrobitUsb}
                            >
                                <FormattedMessage
                                    id="gui.menuBar.microbitUsbConnectAction"
                                    defaultMessage="Forbind med kabel"
                                    description="button label for choosing the serial port and opening the cable connection"
                                />
                            </button>
                        </div>
                    </div>
                    {this.state.microbitUsbSetupStatus ? (
                        <div className={styles.microbitSetupStatus}>
                            {this.state.microbitUsbSetupStatus}
                        </div>
                    ) : null}
                    {this.state.microbitUsbSetupError ? (
                        <div className={styles.microbitSetupError}>
                            {this.state.microbitUsbSetupError}
                        </div>
                    ) : null}
                    <div className={styles.microbitModalFooter}>
                        <button
                            className={styles.microbitModalCloseButton}
                            onClick={this.handleCloseMicrobitUsbSetup}
                        >
                            <FormattedMessage {...ariaMessages.cancelConnectionChoice} />
                        </button>
                    </div>
                </div>
            </ReactModal>
        );
    }
    getSaveToComputerHandler (downloadProjectCallback) {
        return () => {
            this.props.onRequestCloseFile();
            downloadProjectCallback();
            if (this.props.onProjectTelemetryEvent) {
                const metadata = collectMetadata(this.props.vm, this.props.projectTitle, this.props.locale);
                this.props.onProjectTelemetryEvent('projectDidSave', metadata);
            }
        };
    }
    restoreOptionMessage (deletedItem) {
        switch (deletedItem) {
        case 'Sprite':
            return (<FormattedMessage
                defaultMessage="Restore Sprite"
                description="Menu bar item for restoring the last deleted sprite."
                id="gui.menuBar.restoreSprite"
            />);
        case 'Sound':
            return (<FormattedMessage
                defaultMessage="Restore Sound"
                description="Menu bar item for restoring the last deleted sound."
                id="gui.menuBar.restoreSound"
            />);
        case 'Costume':
            return (<FormattedMessage
                defaultMessage="Restore Costume"
                description="Menu bar item for restoring the last deleted costume."
                id="gui.menuBar.restoreCostume"
            />);
        default: {
            return (<FormattedMessage
                defaultMessage="Restore"
                description="Menu bar item for restoring the last deleted item in its disabled state." /* eslint-disable-line max-len */
                id="gui.menuBar.restore"
            />);
        }
        }
    }
    buildAboutMenu (onClickAbout) {
        if (!onClickAbout) {
            // hide the button
            return null;
        }
        if (typeof onClickAbout === 'function') {
            // make a button which calls a function
            return <AboutButton onClick={onClickAbout} />;
        }
        // assume it's an array of objects
        // each item must have a 'title' FormattedMessage and a 'handleClick' function
        // generate a menu with items for each object in the array
        return (
            <div
                className={classNames(styles.menuBarItem, styles.hoverable, {
                    [styles.active]: this.props.aboutMenuOpen
                })}
                onMouseUp={this.props.onRequestOpenAbout}
            >
                <img
                    className={styles.aboutIcon}
                    src={aboutIcon}
                />
                <MenuBarMenu
                    className={classNames(styles.menuBarMenu)}
                    open={this.props.aboutMenuOpen}
                    place={this.props.isRtl ? 'right' : 'left'}
                    onRequestClose={this.props.onRequestCloseAbout}
                >
                    {
                        onClickAbout.map(itemProps => (
                            <MenuItem
                                key={itemProps.title}
                                isRtl={this.props.isRtl}
                                onClick={this.wrapAboutMenuCallback(itemProps.onClick)}
                            >
                                {itemProps.title}
                            </MenuItem>
                        ))
                    }
                </MenuBarMenu>
            </div>
        );
    }
    wrapAboutMenuCallback (callback) {
        return () => {
            callback();
            this.props.onRequestCloseAbout();
        };
    }
    render () {
        const saveNowMessage = (
            <FormattedMessage
                defaultMessage="Save now"
                description="Menu bar item for saving now"
                id="gui.menuBar.saveNow"
            />
        );
        const saveWithModelMessage = this.props.intl.formatMessage(sharedMessages.saveToComputerWithModelTitle);
        const createCopyMessage = (
            <FormattedMessage
                defaultMessage="Save as a copy"
                description="Menu bar item for saving as a copy"
                id="gui.menuBar.saveAsCopy"
            />
        );
        const remixMessage = (
            <FormattedMessage
                defaultMessage="Remix"
                description="Menu bar item for remixing"
                id="gui.menuBar.remix"
            />
        );
        const newProjectMessage = (
            <FormattedMessage
                defaultMessage="New"
                description="Menu bar item for creating a new project"
                id="gui.menuBar.new"
            />
        );
        const remixButton = (
            <Button
                className={classNames(
                    styles.menuBarButton,
                    styles.remixButton
                )}
                iconClassName={styles.remixButtonIcon}
                iconSrc={remixIcon}
                onClick={this.handleClickRemix}
            >
                {remixMessage}
            </Button>
        );
        // Show the About button only if we have a handler for it (like in the desktop app)
        const aboutButton = this.buildAboutMenu(this.props.onClickAbout);
        this.projectBundleDownloadCallback = null;
        return (
            <Box
                className={classNames(
                    this.props.className,
                    styles.menuBar
                )}
            >
                <ProjectBundleDownloader>{(className, downloadProjectBundle) => {
                    this.projectBundleDownloadCallback = downloadProjectBundle;
                    return null;
                }}</ProjectBundleDownloader>
                {this.state.projectBundleConfirmationOpen ? (
                    <ProjectBundleConfirmationModal
                        onCancel={this.handleCancelProjectBundleConfirmation}
                        onConfirm={this.handleConfirmProjectBundleDownload}
                    />
                ) : null}
                <div className={styles.mainMenu}>
                    <div className={styles.fileGroup}>
                        <div className={classNames(styles.menuBarItem)}>
                            <div
                                id="logo_img"
                                className={classNames(styles.funNameBadge, {
                                    [styles.clickable]: typeof this.props.onClickLogo !== 'undefined'
                                })}
                                onClick={this.props.onClickLogo}
                            >
                                <img
                                    alt=""
                                    className={styles.funNameIcon}
                                    draggable={false}
                                    src={datakoderenIcon}
                                />
                                <span className={styles.funNameText}>{'Databyggeren'}</span>
                            </div>
                        </div>
                        {(this.props.canChangeTheme || this.props.canChangeLanguage) && (<SettingsMenu
                            canChangeLanguage={this.props.canChangeLanguage}
                            canChangeTheme={this.props.canChangeTheme}
                            isRtl={this.props.isRtl}
                            onRequestClose={this.props.onRequestCloseSettings}
                            onRequestOpen={this.props.onClickSettings}
                            settingsMenuOpen={this.props.settingsMenuOpen}
                        />)}
                        {(this.props.canManageFiles) && (
                            <div
                                className={classNames(styles.menuBarItem, styles.hoverable, {
                                    [styles.active]: this.props.fileMenuOpen
                                })}
                                onMouseUp={this.props.onClickFile}
                            >
                                <img src={fileIcon} />
                                <span className={styles.collapsibleLabel}>
                                    <FormattedMessage
                                        defaultMessage="File"
                                        description="Text for file dropdown menu"
                                        id="gui.menuBar.file"
                                    />
                                </span>
                                <img src={dropdownCaret} />
                                <MenuBarMenu
                                    className={classNames(styles.menuBarMenu)}
                                    open={this.props.fileMenuOpen}
                                    place={this.props.isRtl ? 'left' : 'right'}
                                    onRequestClose={this.props.onRequestCloseFile}
                                >
                                    <MenuSection>
                                        <MenuItem
                                            isRtl={this.props.isRtl}
                                            onClick={this.handleClickNew}
                                        >
                                            {newProjectMessage}
                                        </MenuItem>
                                    </MenuSection>
                                    {(this.props.canSave || this.props.canCreateCopy || this.props.canRemix) && (
                                        <MenuSection>
                                            {this.props.canSave && (
                                                <MenuItem onClick={this.handleClickSave}>
                                                    {saveNowMessage}
                                                </MenuItem>
                                            )}
                                            {this.props.canCreateCopy && (
                                                <MenuItem onClick={this.handleClickSaveAsCopy}>
                                                    {createCopyMessage}
                                                </MenuItem>
                                            )}
                                            {this.props.canRemix && (
                                                <MenuItem onClick={this.handleClickRemix}>
                                                    {remixMessage}
                                                </MenuItem>
                                            )}
                                        </MenuSection>
                                    )}
                                    <MenuSection>
                                        <MenuItem
                                            onClick={this.props.onStartSelectingFileUpload}
                                        >
                                            {this.props.intl.formatMessage(sharedMessages.loadFromComputerTitle)}
                                        </MenuItem>
                                        <MenuItem onClick={this.handleOpenProjectBundleConfirmation}>
                                            {saveWithModelMessage}
                                        </MenuItem>
                                        <SB3Downloader>{(className, downloadProjectCallback) => (
                                            <MenuItem
                                                className={className}
                                                onClick={this.getSaveToComputerHandler(downloadProjectCallback)}
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Gem pÃ¥ din computer uden model"
                                                    description="Menu bar item for downloading a project to your computer" // eslint-disable-line max-len
                                                    id="gui.menuBar.downloadToComputer"
                                                />
                                            </MenuItem>
                                        )}</SB3Downloader>
                                    </MenuSection>
                                </MenuBarMenu>
                            </div>
                        )}
                        <div
                            className={classNames(styles.menuBarItem, styles.hoverable, {
                                [styles.active]: this.props.editMenuOpen
                            })}
                            onMouseUp={this.props.onClickEdit}
                        >
                            <img src={editIcon} />
                            <span className={styles.collapsibleLabel}>
                                <FormattedMessage
                                    defaultMessage="Edit"
                                    description="Text for edit dropdown menu"
                                    id="gui.menuBar.edit"
                                />
                            </span>
                            <img src={dropdownCaret} />
                            <MenuBarMenu
                                className={classNames(styles.menuBarMenu)}
                                open={this.props.editMenuOpen}
                                place={this.props.isRtl ? 'left' : 'right'}
                                onRequestClose={this.props.onRequestCloseEdit}
                            >
                                <DeletionRestorer>{(handleRestore, {restorable, deletedItem}) => (
                                    <MenuItem
                                        className={classNames({[styles.disabled]: !restorable})}
                                        onClick={this.handleRestoreOption(handleRestore)}
                                    >
                                        {this.restoreOptionMessage(deletedItem)}
                                    </MenuItem>
                                )}</DeletionRestorer>
                                <MenuSection>
                                    <TurboMode>{(toggleTurboMode, {turboMode}) => (
                                        <MenuItem onClick={toggleTurboMode}>
                                            {turboMode ? (
                                                <FormattedMessage
                                                    defaultMessage="Turn off Turbo Mode"
                                                    description="Menu bar item for turning off turbo mode"
                                                    id="gui.menuBar.turboModeOff"
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    defaultMessage="Turn on Turbo Mode"
                                                    description="Menu bar item for turning on turbo mode"
                                                    id="gui.menuBar.turboModeOn"
                                                />
                                            )}
                                        </MenuItem>
                                    )}</TurboMode>
                                </MenuSection>
                            </MenuBarMenu>

                        </div>
                        {this.props.isTotallyNormal && (
                            <div
                                className={classNames(styles.menuBarItem, styles.hoverable, {
                                    [styles.active]: this.props.modeMenuOpen
                                })}
                                onMouseUp={this.props.onClickMode}
                            >
                                <div className={classNames(styles.editMenu)}>
                                    <FormattedMessage
                                        defaultMessage="Mode"
                                        description="Mode menu item in the menu bar"
                                        id="gui.menuBar.modeMenu"
                                    />
                                </div>
                                <MenuBarMenu
                                    className={classNames(styles.menuBarMenu)}
                                    open={this.props.modeMenuOpen}
                                    place={this.props.isRtl ? 'left' : 'right'}
                                    onRequestClose={this.props.onRequestCloseMode}
                                >
                                    <MenuSection>
                                        <MenuItem onClick={this.handleSetMode('NOW')}>
                                            <span className={classNames({[styles.inactive]: !this.props.modeNow})}>
                                                {'âœ“'}
                                            </span>
                                            {' '}
                                            <FormattedMessage
                                                defaultMessage="Normal mode"
                                                description="April fools: resets editor to not have any pranks"
                                                id="gui.menuBar.normalMode"
                                            />
                                        </MenuItem>
                                        <MenuItem onClick={this.handleSetMode('2020')}>
                                            <span className={classNames({[styles.inactive]: !this.props.mode2020})}>
                                                {'âœ“'}
                                            </span>
                                            {' '}
                                            <FormattedMessage
                                                defaultMessage="Caturday mode"
                                                description="April fools: Cat blocks mode"
                                                id="gui.menuBar.caturdayMode"
                                            />
                                        </MenuItem>
                                    </MenuSection>
                                </MenuBarMenu>
                            </div>
                        )}
                    </div>
                    {this.props.canEditTitle ? (
                        <div className={classNames(styles.menuBarItem, styles.growable)}>
                            <MenuBarItemTooltip
                                enable
                                id="title-field"
                            >
                                <ProjectTitleInput
                                    className={classNames(styles.titleFieldGrowable)}
                                />
                            </MenuBarItemTooltip>
                        </div>
                    ) : ((this.props.authorUsername && this.props.authorUsername !== this.props.username) ? (
                        <AuthorInfo
                            className={styles.authorInfo}
                            imageUrl={this.props.authorThumbnailUrl}
                            projectTitle={this.props.projectTitle}
                            userId={this.props.authorId}
                            username={this.props.authorUsername}
                        />
                    ) : null)}
                    <div className={classNames(styles.menuBarItem)}>
                        {this.props.canRemix ? remixButton : []}
                    </div>
                    <Divider className={classNames(styles.divider)} />
                    <div className={styles.fileGroup}>
                        <div
                            aria-label={this.props.intl.formatMessage(ariaMessages.dataTrainer)}
                            className={classNames(
                                styles.menuBarItem,
                                styles.noOffset,
                                styles.hoverable,
                                styles.dataTrainerButton
                            )}
                            onClick={this.props.onOpenVisionTrainer}
                        >
                            <img
                                className={classNames(styles.helpIcon, styles.dataTrainerIcon)}
                                src={dataTrainerIcon}
                            />
                            <span className={classNames(styles.debugLabel, styles.dataTrainerLabel)}>
                                <FormattedMessage {...ariaMessages.dataTrainer} />
                            </span>
                        </div>
                        <div className={styles.quickAccessGroup}>
                            <div
                                aria-label={this.props.intl.formatMessage(ariaMessages.codeLibrary)}
                                className={classNames(
                                    styles.menuBarItem,
                                    styles.noOffset,
                                    styles.hoverable,
                                    styles.dataTrainerButton
                                )}
                                onClick={this.props.onOpenCodeLibrary}
                            >
                                <img
                                    className={classNames(styles.helpIcon, styles.dataTrainerIcon)}
                                    src={codeLibraryIcon}
                                />
                                <span className={classNames(styles.debugLabel, styles.dataTrainerLabel)}>
                                    <FormattedMessage {...ariaMessages.codeLibrary} />
                                </span>
                            </div>
                            <div
                                aria-label={this.props.intl.formatMessage(ariaMessages.connectMicrobit)}
                                className={classNames(
                                    styles.menuBarItem,
                                    styles.noOffset,
                                    styles.hoverable,
                                    styles.dataTrainerButton,
                                    styles.microbitMenuButton,
                                    {[styles.active]: this.state.microbitUsbSetupOpen}
                                )}
                                onClick={this.handleOpenMicrobitMenu}
                            >
                                <img
                                    className={classNames(styles.helpIcon, styles.dataTrainerIcon)}
                                    src={microbitIcon}
                                />
                                <span className={classNames(styles.debugLabel, styles.dataTrainerLabel)}>
                                    <FormattedMessage
                                        id="gui.menuBar.connectMicrobitMenu"
                                        defaultMessage="Forbind Micro:bit"
                                        description="label for the micro:bit connection chooser button"
                                    />
                                </span>
                            </div>
                        </div>
                        <div
                            aria-label={this.props.intl.formatMessage(ariaMessages.tutorials)}
                            className={
                                classNames(styles.menuBarItem, styles.noOffset, styles.hoverable, 'tutorials-button')
                            }
                            onClick={this.props.onOpenTipLibrary}
                        >
                            <img
                                className={styles.helpIcon}
                                src={helpIcon}
                            />
                            <span className={styles.tutorialsLabel}>
                                <FormattedMessage {...ariaMessages.tutorials} />
                            </span>
                        </div>
                        <div
                            aria-label={this.props.intl.formatMessage(ariaMessages.debug)}
                            className={classNames(styles.menuBarItem, styles.noOffset, styles.hoverable)}
                            onClick={this.props.onOpenDebugModal}
                        >
                            <img
                                className={styles.helpIcon}
                                src={debugIcon}
                            />
                            <span className={styles.debugLabel}>
                                <FormattedMessage {...ariaMessages.debug} />
                            </span>
                        </div>
                    </div>
                    {/* Based on Scratch by MIT Media Lab */}
                    {/* Licensed under the MIT License */}
                    <div className={classNames(styles.menuBarItem, styles.basedOnCredit)}>
                        <span>{'Based on Scratch by MIT Media Lab'}</span>
                        <span>{'Created by Ian Stenz'}</span>
                    </div>
                </div>

                {/* show the proper UI in the account menu, given whether the user is
                logged in, and whether a session is available to log in with */}
                <div className={styles.accountInfoGroup}>
                    {this.props.sessionExists ? (
                        this.props.username ? (
                            // ************ user is logged in ************
                            <React.Fragment />
                        ) : (
                            // ********* user not logged in, but a session exists
                            // ********* so they can choose to log in
                            <React.Fragment>
                                <div
                                    className={classNames(
                                        styles.menuBarItem,
                                        styles.hoverable
                                    )}
                                    key="join"
                                    onMouseUp={this.props.onOpenRegistration}
                                >
                                    <FormattedMessage
                                        defaultMessage="Join Scratch"
                                        description="Link for creating a Scratch account"
                                        id="gui.menuBar.joinScratch"
                                    />
                                </div>
                                <div
                                    className={classNames(
                                        styles.menuBarItem,
                                        styles.hoverable
                                    )}
                                    key="login"
                                    onMouseUp={this.props.onClickLogin}
                                >
                                    <FormattedMessage
                                        defaultMessage="Sign in"
                                        description="Link for signing in to your Scratch account"
                                        id="gui.menuBar.signIn"
                                    />
                                    <LoginDropdown
                                        className={classNames(styles.menuBarMenu)}
                                        isOpen={this.props.loginMenuOpen}
                                        isRtl={this.props.isRtl}
                                        renderLogin={this.props.renderLogin}
                                        onClose={this.props.onRequestCloseLogin}
                                    />
                                </div>
                            </React.Fragment>
                        )
                    ) : (
                        // ******** no login session is available, so don't show login stuff
                        <React.Fragment>
                            {this.props.showComingSoon ? null : []}
                        </React.Fragment>
                    )}
                </div>

                {aboutButton}
                {this.renderMicrobitUsbSetupModal()}
            </Box>
        );
    }
}

MenuBar.propTypes = {
    aboutMenuOpen: PropTypes.bool,
    accountMenuOpen: PropTypes.bool,
    authorId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    authorThumbnailUrl: PropTypes.string,
    authorUsername: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    autoUpdateProject: PropTypes.func,
    canChangeLanguage: PropTypes.bool,
    canChangeTheme: PropTypes.bool,
    canCreateCopy: PropTypes.bool,
    canCreateNew: PropTypes.bool,
    canEditTitle: PropTypes.bool,
    canManageFiles: PropTypes.bool,
    canRemix: PropTypes.bool,
    canSave: PropTypes.bool,
    canShare: PropTypes.bool,
    className: PropTypes.string,
    confirmReadyToReplaceProject: PropTypes.func,
    currentLocale: PropTypes.string.isRequired,
    editMenuOpen: PropTypes.bool,
    enableCommunity: PropTypes.bool,
    fileMenuOpen: PropTypes.bool,
    intl: intlShape,
    isRtl: PropTypes.bool,
    isShared: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    isTotallyNormal: PropTypes.bool,
    isUpdating: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    loginMenuOpen: PropTypes.bool,
    logo: PropTypes.string,
    mode1920: PropTypes.bool,
    mode1990: PropTypes.bool,
    mode2020: PropTypes.bool,
    mode220022BC: PropTypes.bool,
    modeMenuOpen: PropTypes.bool,
    modeNow: PropTypes.bool,
    onClickAbout: PropTypes.oneOfType([
        PropTypes.func, // button mode: call this callback when the About button is clicked
        PropTypes.arrayOf( // menu mode: list of items in the About menu
            PropTypes.shape({
                title: PropTypes.string, // text for the menu item
                onClick: PropTypes.func // call this callback when the menu item is clicked
            })
        )
    ]),
    onClickAccount: PropTypes.func,
    onClickEdit: PropTypes.func,
    onClickFile: PropTypes.func,
    onClickLogin: PropTypes.func,
    onClickLogo: PropTypes.func,
    onClickMode: PropTypes.func,
    onClickNew: PropTypes.func,
    onClickRemix: PropTypes.func,
    onClickSave: PropTypes.func,
    onClickSaveAsCopy: PropTypes.func,
    onClickSettings: PropTypes.func,
    onLogOut: PropTypes.func,
    onMicrobitButtonClick: PropTypes.func,
    onMicrobitBleButtonClick: PropTypes.func,
    onPrepareMicrobitPeripheral: PropTypes.func,
    onOpenRegistration: PropTypes.func,
    onOpenTipLibrary: PropTypes.func,
    onOpenDebugModal: PropTypes.func,
    onOpenCodeLibrary: PropTypes.func,
    onOpenVisionTrainer: PropTypes.func,
    onProjectTelemetryEvent: PropTypes.func,
    onRequestCloseAbout: PropTypes.func,
    onRequestCloseAccount: PropTypes.func,
    onRequestCloseEdit: PropTypes.func,
    onRequestCloseFile: PropTypes.func,
    onRequestCloseLogin: PropTypes.func,
    onRequestCloseMode: PropTypes.func,
    onRequestCloseSettings: PropTypes.func,
    onRequestOpenAbout: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onSetTimeTravelMode: PropTypes.func,
    onShare: PropTypes.func,
    onStartSelectingFileUpload: PropTypes.func,
    onToggleLoginOpen: PropTypes.func,
    projectTitle: PropTypes.string,
    renderLogin: PropTypes.func,
    sessionExists: PropTypes.bool,
    settingsMenuOpen: PropTypes.bool,
    shouldSaveBeforeTransition: PropTypes.func,
    showComingSoon: PropTypes.bool,
    username: PropTypes.string,
    userOwnsProject: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired
};

MenuBar.defaultProps = {
    logo: scratchLogo,
    onShare: () => {}
};

const mapStateToProps = (state, ownProps) => {
    const loadingState = state.scratchGui.projectState.loadingState;
    const user = state.session && state.session.session && state.session.session.user;
    return {
        aboutMenuOpen: aboutMenuOpen(state),
        accountMenuOpen: accountMenuOpen(state),
        currentLocale: state.locales.locale,
        fileMenuOpen: fileMenuOpen(state),
        editMenuOpen: editMenuOpen(state),
        isRtl: state.locales.isRtl,
        isUpdating: getIsUpdating(loadingState),
        isShowingProject: getIsShowingProject(loadingState),
        locale: state.locales.locale,
        loginMenuOpen: loginMenuOpen(state),
        modeMenuOpen: modeMenuOpen(state),
        projectTitle: state.scratchGui.projectTitle,
        sessionExists: state.session && typeof state.session.session !== 'undefined',
        settingsMenuOpen: settingsMenuOpen(state),
        username: user ? user.username : null,
        userOwnsProject: ownProps.authorUsername && user &&
            (ownProps.authorUsername === user.username),
        vm: state.scratchGui.vm,
        mode220022BC: isTimeTravel220022BC(state),
        mode1920: isTimeTravel1920(state),
        mode1990: isTimeTravel1990(state),
        mode2020: isTimeTravel2020(state),
        modeNow: isTimeTravelNow(state)
    };
};

const mapDispatchToProps = dispatch => ({
    autoUpdateProject: () => dispatch(autoUpdateProject()),
    onOpenTipLibrary: () => dispatch(openTipsLibrary()),
    onOpenDebugModal: () => dispatch(openDebugModal()),
    onOpenCodeLibrary: () => dispatch(openCodeLibrary()),
    onOpenVisionTrainer: () => dispatch(openVisionTrainer()),
    onClickAccount: () => dispatch(openAccountMenu()),
    onRequestCloseAccount: () => dispatch(closeAccountMenu()),
    onClickFile: () => dispatch(openFileMenu()),
    onRequestCloseFile: () => dispatch(closeFileMenu()),
    onClickEdit: () => dispatch(openEditMenu()),
    onRequestCloseEdit: () => dispatch(closeEditMenu()),
    onClickLogin: () => dispatch(openLoginMenu()),
    onRequestCloseLogin: () => dispatch(closeLoginMenu()),
    onClickMode: () => dispatch(openModeMenu()),
    onRequestCloseMode: () => dispatch(closeModeMenu()),
    onRequestOpenAbout: () => dispatch(openAboutMenu()),
    onRequestCloseAbout: () => dispatch(closeAboutMenu()),
    onClickSettings: () => dispatch(openSettingsMenu()),
    onRequestCloseSettings: () => dispatch(closeSettingsMenu()),
    onClickNew: needSave => dispatch(requestNewProject(needSave)),
    onClickRemix: () => dispatch(remixProject()),
    onClickSave: () => dispatch(manualUpdateProject()),
    onClickSaveAsCopy: () => dispatch(saveProjectAsCopy()),
    onSeeCommunity: () => dispatch(setPlayer(true)),
    onSetTimeTravelMode: mode => dispatch(setTimeTravel(mode))
});

export default compose(
    injectIntl,
    MenuBarHOC,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(MenuBar);

